import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ProductItem, getProductBySlug, launchUrl } from "../data/productsData";
import { loadProduct, getCachedProduct } from "../services/productSource";
import Error from "./404";
import Seo, { softwareSchema } from "../components/Seo";
import "../styles/revamp.css";

const ProductDetails: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [product, setProduct] = useState<ProductItem | undefined>(() =>
        slug ? getCachedProduct(slug) : undefined
    );
    const [checked, setChecked] = useState<boolean>(() => Boolean(product));

    useEffect(() => {
        if (!slug) {
            setChecked(true);
            return;
        }
        let cancelled = false;
        loadProduct(slug).then((next) => {
            if (!cancelled) {
                setProduct(next);
                setChecked(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (!checked) {
        return (
            <main className="mx-detail" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div className="container" style={{ textAlign: "center", padding: "80px 0" }}>
                    <p className="mx-eyebrow">Loading...</p>
                </div>
            </main>
        );
    }

    if (!product) return <Error />;

    return (
        <>
        <Seo
            title={product.name}
            description={product.tagline}
            path={`/products/${product.slug}`}
            structuredData={softwareSchema({
                name: product.name,
                description: product.summary,
                category: product.category,
                path: `/products/${product.slug}`,
            })}
        />
        <main className="mx-detail" style={{ ["--mx-dot-color" as any]: product.accent }}>
            <div className="container">
                <nav className="mx-crumb" aria-label="Breadcrumb">
                    <Link to="/products">Platform</Link>
                    <span aria-hidden="true">/</span>
                    <span>{product.name}</span>
                </nav>

                <header className="mx-detail-head">
                    <div>
                        <p className="mx-eyebrow">
                            <span className="mx-dot" /> {product.category}
                        </p>
                        <h1 className="mx-detail-title">{product.name}</h1>
                        <p className="mx-detail-tagline">{product.tagline}</p>
                    </div>

                    {launchUrl(product) && (
                        <a
                            className="mx-btn mx-btn-primary"
                            href={launchUrl(product)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${product.name} in a new tab`}
                        >
                            Open {product.name} ↗
                        </a>
                    )}
                </header>

                <ul className="mx-detail-metrics">
                    {product.metrics.map((m) => (
                        <li key={m.label}>
                            <span className="mx-metric-value">{m.value}</span>
                            <span className="mx-metric-label">{m.label}</span>
                        </li>
                    ))}
                </ul>

                <section className="mx-detail-split">
                    <div>
                        <p className="mx-eyebrow">The problem</p>
                        <p className="mx-detail-prose">{product.problem}</p>
                    </div>
                    <div>
                        <p className="mx-eyebrow">What changes</p>
                        <p className="mx-detail-prose">{product.solution}</p>
                    </div>
                </section>

                <section className="mx-detail-section">
                    <p className="mx-eyebrow">Overview</p>
                    <p className="mx-detail-prose mx-detail-summary">{product.summary}</p>
                </section>

                <section className="mx-detail-section">
                    <p className="mx-eyebrow">Capabilities</p>
                    <div className="mx-feature-grid">
                        {product.features.map((f) => (
                            <div className="mx-card" key={f.title}>
                                <h3 className="mx-card-title">{f.title}</h3>
                                <p className="mx-card-body">{f.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mx-detail-cta">
                    <h2 className="mx-card-title">See it running on your own data.</h2>
                    <div className="mx-suite-actions">
                        <Link className="mx-btn mx-btn-primary" to="/contact">
                            Book a demo
                        </Link>
                        <Link className="mx-btn mx-btn-ghost" to="/products">
                            All products
                        </Link>
                    </div>
                </section>
            </div>
        </main>
        </>
    );
};

export default ProductDetails;
